<template>
  <modal
    :show="isVisible"
    :title="t('Detalhes da taxa de implantação')"
    elem-id="modal_setup_fee_detail"
    size="compactDesktop"
    :close-label="t('Fechar')"
    @close="closeModal"
    @dismiss="closeModal"
  >
    <template v-if="isVisible">
      <div data-testid="setup-fee-detail-modal">
        <compact-data-table :value="detailRows" />

        <div class="setup-fee-detail-actions">
          <Button
            v-if="canMarkPaid"
            :label="t('Marcar como paga')"
            icon="pi pi-check"
            severity="success"
            size="small"
            data-testid="setup-fee-detail-mark-paid"
            @click="handleMarkPaid"
          />
          <Button
            v-if="canSync"
            :label="t('Sincronizar com o provedor')"
            icon="pi pi-sync"
            severity="secondary"
            size="small"
            :loading="isSyncing"
            :disabled="isSyncing"
            data-testid="setup-fee-detail-sync"
            @click="handleSync"
          />
        </div>
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { eventBus } from '../../../eventBus'
import { log } from '../../../utils/DebugLogger'
import { useReconciliationSync } from '../../../composables/useReconciliationSync'
import { permissionsState } from '../../../services/permissionsService'
import Modal from '../../../ui/Modal.vue'
import CompactDataTable from '../../../grid/CompactDataTable.vue'
import Button from '../../../ui/AppButton.vue'

interface SetupFeeDetailRow {
  id?: number | string
  franquia_id?: number | string
  franchise_name?: string
  amount_display?: string
  provider?: string
  provider_display?: string
  provider_charge_id?: string
  status?: string
  paid_at_display?: string
  created_at_display?: string
}

interface ModalOpenPayload {
  row?: SetupFeeDetailRow
  franquiaId?: number | string
  franchiseName?: string
}

const { t } = useI18n()
const { syncSetupFee } = useReconciliationSync()

const isVisible = ref(false)
const isSyncing = ref(false)
const currentRow = ref<SetupFeeDetailRow | null>(null)
const currentFranquiaId = ref<number | null>(null)
const currentFranchiseName = ref<string>('')

const statusLabelMap: Record<string, string> = {
  pending: t('Pendente'),
  confirmed: t('Confirmada'),
  paid: t('Paga'),
  failed: t('Falhou'),
  exempt: t('Isenta'),
  expired: t('Abandonada'),
  superseded: t('Substituída'),
}

const detailRows = computed(() => {
  const row = currentRow.value
  if (!row) return []

  const statusLabel = row.status ? (statusLabelMap[row.status] ?? row.status) : '—'

  return [
    { prop: t('ID'), value: row.id ?? '—' },
    { prop: t('Franquia'), value: currentFranchiseName.value || row.franchise_name || row.franquia_id || currentFranquiaId.value || '—' },
    { prop: t('Valor'), value: row.amount_display ?? '—' },
    { prop: t('Gateway'), value: row.provider_display || row.provider || '—' },
    { prop: t('ID no Gateway'), value: row.provider_charge_id ?? '—', copyable: true, testId: 'setup-fee-charge-id' },
    { prop: t('Situação'), value: statusLabel },
    { prop: t('Emitido em'), value: row.created_at_display ?? '—' },
    { prop: t('Pago em'), value: row.paid_at_display ?? '—' },
  ]
})

/**
 * Staff-only actions gated by the USER'S GROUP, not by who opened the modal:
 * this modal also opens from the USER Billing 360's charges table, and both
 * mark-paid and sync are Full-only backend endpoints (assertFullOnly). A
 * Franqueado must never be offered a button the backend will refuse.
 */
const isFullUser = computed<boolean>(() => (permissionsState.user?.groups ?? []).includes('Full'))

const canMarkPaid = computed(() => {
  const st = currentRow.value?.status
  return isFullUser.value && (st === 'pending' || st === 'owed')
})

const canSync = computed(() => {
  const st = currentRow.value?.status
  return isFullUser.value && st === 'pending'
})

async function handleSync(): Promise<void> {
  const fid = currentFranquiaId.value ?? Number(currentRow.value?.franquia_id)
  if (!fid || isSyncing.value) return

  isSyncing.value = true
  try {
    const result = await syncSetupFee(fid)
    eventBus.emit('modal.reconciliation-result.open', {
      title: `${t('Taxa de Implantação')} - ${t('Franquia')} #${fid}`,
      rows: result.rows,
      truncated: false,
    })
    eventBus.emit('globalMessage.success', t('Taxa de implantação sincronizada com o provedor'))
    eventBus.emit('setupFee.saved')
    closeModal()
  } catch (err) {
    log('SetupFeeDetailModal', 'handleSync', 'Erro ao sincronizar taxa', err)
    eventBus.emit('globalMessage.error', t('Erro ao sincronizar taxa de implantação'))
  } finally {
    isSyncing.value = false
  }
}

function handleMarkPaid(): void {
  const fid = currentFranquiaId.value ?? Number(currentRow.value?.franquia_id)
  if (!fid) return
  eventBus.emit('modal.setup-fee-mark-paid.open', {
    franquiaId: fid,
    franchiseName: currentFranchiseName.value || currentRow.value?.franchise_name,
  })
}

function onOpen(payload?: ModalOpenPayload): void {
  log('SetupFeeDetailModal', 'onOpen', 'Abrindo detalhe da taxa', payload)
  currentRow.value = payload?.row ?? null
  currentFranquiaId.value = payload?.franquiaId ? Number(payload.franquiaId) : (payload?.row?.franquia_id ? Number(payload.row.franquia_id) : null)
  currentFranchiseName.value = payload?.franchiseName ?? payload?.row?.franchise_name ?? ''
  isVisible.value = true
}

function closeModal(): void {
  log('SetupFeeDetailModal', 'closeModal', 'Fechando detalhe da taxa')
  isVisible.value = false
  currentRow.value = null
  eventBus.emit('modal.setup-fee-detail.close')
}

onMounted(() => {
  eventBus.on('modal.setup-fee-detail.open', onOpen as (data?: unknown) => void)
})

onBeforeUnmount(() => {
  eventBus.off('modal.setup-fee-detail.open', onOpen as (data?: unknown) => void)
})
</script>

<style scoped>
.setup-fee-detail-actions {
  display: flex;
  gap: 0.75rem;
  margin-top: 1rem;
  justify-content: flex-end;
}
</style>
